import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import type { Media, PlaylistItem } from '../../types';

export interface BrowserPlayerItem {
  media: Media;
  playlistItem?: PlaylistItem | null;
  src: string;
  initialSeekSeconds?: number;
  displayDurationSeconds?: number;
}

export interface BrowserPlaylistPlayerHandle {
  play: () => Promise<void>;
  pause: () => void;
  stop: () => void;
  seek: (seconds: number) => void;
  getCurrentTime: () => number;
  captureScreenshot: (options?: { width?: number; height?: number; quality?: number }) => Promise<{
    base64_data: string;
    file_size: number;
    mime_type: string;
  }>;
}

interface BrowserPlaylistPlayerProps {
  item: BrowserPlayerItem | null;
  playing: boolean;
  muted: boolean;
  volume: number;
  onEnded: () => void;
  onError: (message: string) => void;
}

const getMediaType = (media?: Media): string => {
  const rawType = media?.media_type || media?.type;
  return String(rawType || '').toLowerCase();
};

const getFit = (fitMode?: string) => {
  if (fitMode === 'stretch') return 'fill';
  if (fitMode === 'fit') return 'contain';
  return 'cover';
};

const resolveCanvasSize = (
  naturalWidth?: number,
  naturalHeight?: number,
  rectWidth?: number,
  rectHeight?: number,
  requestedWidth?: number,
  requestedHeight?: number,
) => {
  const sourceWidth = Math.max(1, Math.round(naturalWidth || rectWidth || 1280));
  const sourceHeight = Math.max(1, Math.round(naturalHeight || rectHeight || 720));
  const aspectRatio = sourceWidth / sourceHeight;

  if (requestedWidth && requestedHeight) {
    return { width: requestedWidth, height: requestedHeight };
  }
  if (requestedWidth) {
    return { width: requestedWidth, height: Math.max(1, Math.round(requestedWidth / aspectRatio)) };
  }
  if (requestedHeight) {
    return { width: Math.max(1, Math.round(requestedHeight * aspectRatio)), height: requestedHeight };
  }

  const maxWidth = 1280;
  const maxHeight = 720;
  const scale = Math.min(1, maxWidth / sourceWidth, maxHeight / sourceHeight);
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
};

const BrowserPlaylistPlayer = forwardRef<BrowserPlaylistPlayerHandle, BrowserPlaylistPlayerProps>(
  ({ item, playing, muted, volume, onEnded, onError }, ref) => {
    const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const imageTimerRef = useRef<number | null>(null);

    const mediaType = useMemo(() => getMediaType(item?.media), [item]);
    const effectiveDuration = item?.displayDurationSeconds || item?.playlistItem?.custom_duration || item?.media.duration || 10;
    const objectFit = getFit(item?.playlistItem?.fit_mode);

    const clearImageTimer = () => {
      if (imageTimerRef.current) {
        window.clearTimeout(imageTimerRef.current);
        imageTimerRef.current = null;
      }
    };

    useImperativeHandle(ref, () => ({
      async play() {
        if (mediaRef.current) {
          await mediaRef.current.play();
        }
      },
      pause() {
        mediaRef.current?.pause();
      },
      stop() {
        if (mediaRef.current) {
          mediaRef.current.pause();
          mediaRef.current.currentTime = 0;
        }
        clearImageTimer();
      },
      seek(seconds: number) {
        if (mediaRef.current) {
          mediaRef.current.currentTime = Math.max(0, seconds);
        }
      },
      getCurrentTime() {
        return mediaRef.current?.currentTime || 0;
      },
      async captureScreenshot(options) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Canvas недоступен');
        }

        const source = mediaType === 'image' ? imageRef.current : mediaRef.current;
        const rect = source?.getBoundingClientRect();
        const naturalWidth = mediaType === 'image'
          ? imageRef.current?.naturalWidth
          : mediaRef.current instanceof HTMLVideoElement
            ? mediaRef.current.videoWidth
            : undefined;
        const naturalHeight = mediaType === 'image'
          ? imageRef.current?.naturalHeight
          : mediaRef.current instanceof HTMLVideoElement
            ? mediaRef.current.videoHeight
            : undefined;

        const canvasSize = resolveCanvasSize(
          naturalWidth,
          naturalHeight,
          rect?.width,
          rect?.height,
          options?.width,
          options?.height,
        );
        canvas.width = canvasSize.width;
        canvas.height = canvasSize.height;

        context.fillStyle = '#000';
        context.fillRect(0, 0, canvas.width, canvas.height);

        if (mediaType === 'video' && mediaRef.current instanceof HTMLVideoElement) {
          context.drawImage(mediaRef.current, 0, 0, canvas.width, canvas.height);
        } else if (mediaType === 'image' && imageRef.current) {
          context.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
        } else if (mediaType === 'audio') {
          context.fillStyle = '#fff';
          context.font = '32px sans-serif';
          context.textAlign = 'center';
          context.textBaseline = 'middle';
          context.fillText(item?.media.title || 'Audio playback', canvas.width / 2, canvas.height / 2);
        } else {
          throw new Error('Скриншот этого типа медиа в браузере не поддержан');
        }

        const jpegQuality = Math.max(0.1, Math.min(0.92, (options?.quality || 80) / 100));
        const dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
        const base64_data = dataUrl.split(',', 2)[1] || '';
        const file_size = Math.floor((base64_data.length * 3) / 4);
        return {
          base64_data,
          file_size,
          mime_type: 'image/jpeg',
        };
      },
    }));

    useEffect(() => {
      clearImageTimer();

      if (!item || !playing) {
        mediaRef.current?.pause();
        return;
      }

      if (mediaType === 'image') {
        imageTimerRef.current = window.setTimeout(onEnded, Math.max(1, effectiveDuration) * 1000);
        return clearImageTimer;
      }

      if (mediaType === 'video' || mediaType === 'audio') {
        if (item.initialSeekSeconds && mediaRef.current) {
          mediaRef.current.currentTime = Math.max(0, item.initialSeekSeconds);
        }
        mediaRef.current
          ?.play()
          .catch((error) => onError(error?.message || 'Autoplay blocked'));
      }

      return clearImageTimer;
    }, [effectiveDuration, item, mediaType, onEnded, onError, playing]);

    useEffect(() => {
      if (mediaRef.current) {
        mediaRef.current.muted = muted;
        mediaRef.current.volume = Math.min(1, Math.max(0, volume / 100));
      }
    }, [muted, volume, item]);

    if (!item) {
      return (
        <Box sx={{ display: 'grid', placeItems: 'center', width: '100%', height: '100%', color: '#8b949e' }}>
          <Typography variant="h6">Ожидание команды или расписания</Typography>
        </Box>
      );
    }

    if (mediaType === 'video') {
      return (
        <video
          ref={mediaRef as React.RefObject<HTMLVideoElement>}
          src={item.src}
          muted={muted}
          crossOrigin="anonymous"
          playsInline
          autoPlay
          onLoadedMetadata={() => {
            if (item.initialSeekSeconds && mediaRef.current) {
              mediaRef.current.currentTime = Math.max(0, item.initialSeekSeconds);
            }
          }}
          onEnded={onEnded}
          onError={() => onError('Не удалось воспроизвести видео')}
          style={{ width: '100%', height: '100%', objectFit, background: '#000' }}
        />
      );
    }

    if (mediaType === 'audio') {
      return (
        <Box sx={{ display: 'grid', placeItems: 'center', width: '100%', height: '100%', color: '#fff' }}>
          <audio
            ref={mediaRef as React.RefObject<HTMLAudioElement>}
            src={item.src}
            muted={muted}
            crossOrigin="anonymous"
            autoPlay
            onLoadedMetadata={() => {
              if (item.initialSeekSeconds && mediaRef.current) {
                mediaRef.current.currentTime = Math.max(0, item.initialSeekSeconds);
              }
            }}
            onEnded={onEnded}
            onError={() => onError('Не удалось воспроизвести аудио')}
          />
          <Typography variant="h4">{item.media.title}</Typography>
        </Box>
      );
    }

    if (mediaType === 'image') {
      return (
        <img
          ref={imageRef}
          src={item.src}
          alt={item.media.title}
          crossOrigin="anonymous"
          onError={() => onError('Не удалось загрузить изображение')}
          style={{ width: '100%', height: '100%', objectFit, background: '#000' }}
        />
      );
    }

    if (mediaType === 'html' || mediaType === 'pdf') {
      return (
        <iframe
          src={item.src}
          title={item.media.title}
          sandbox="allow-scripts allow-same-origin"
          style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
        />
      );
    }

    return (
      <Box sx={{ display: 'grid', placeItems: 'center', width: '100%', height: '100%', color: '#fff' }}>
        <Typography variant="h6">Формат медиа не поддерживается в браузерном плеере</Typography>
      </Box>
    );
  },
);

BrowserPlaylistPlayer.displayName = 'BrowserPlaylistPlayer';

export default BrowserPlaylistPlayer;
